<aura-dialog-layout [title]="'Décision sur la vérification d\'identité'">
  <div dialog-content class="flex flex-col gap-5 max-w-xl">
    <!-- Rappel des données en présence -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 p-3 rounded-xl bg-neutral-2 border border-neutral-4">
      <div class="flex flex-col gap-0.5">
        <span class="text-[11px] font-bold uppercase tracking-wider text-neutral-10">Profil actuel</span>
        <div class="flex items-center gap-1.5 text-sm font-semibold text-neutral-12">
          <mat-icon class="text-neutral-9 text-base! w-4! h-4!">person</mat-icon>
          <span
            >{{ data.user.identity.firstName | titlecase }}
            {{ data.user.identity.lastName | titlecase }}</span
          >
        </div>
      </div>

      <div class="flex flex-col gap-0.5">
        <span class="text-[11px] font-bold uppercase tracking-wider text-neutral-10"
          >Pièce d'identité Stripe</span
        >
        <div class="flex items-center gap-1.5 text-sm font-semibold text-neutral-12">
          <mat-icon class="text-neutral-9 text-base! w-4! h-4!">badge</mat-icon>
          @if (data.stripeIdentity?.firstName || data.stripeIdentity?.lastName) {
            <span>
              {{ data.stripeIdentity?.firstName | titlecase }} {{ data.stripeIdentity?.lastName | titlecase }}
            </span>
            @if (data.stripeIdentity?.issuingCountry) {
              <span class="text-xs text-neutral-10 uppercase"
                >({{ data.stripeIdentity?.issuingCountry }})</span
              >
            }
          } @else {
            <span class="text-xs text-neutral-9 italic">Aucune donnée de document</span>
          }
        </div>
      </div>
    </div>

    <!-- Score de correspondance automatique -->
    @if (matchResult(); as match) {
      <div
        class="flex items-center justify-between p-3 rounded-xl border text-xs font-semibold"
        [class]="matchBadgeClass()"
      >
        <div class="flex items-center gap-2">
          <mat-icon class="text-base! w-4! h-4!">
            {{ match.score >= 85 ? 'verified' : match.score >= 70 ? 'warning' : 'dangerous' }}
          </mat-icon>
          <span>Score de similarité : {{ match.score }}%</span>
        </div>
        <div class="flex items-center gap-3 text-[11px] font-normal opacity-90">
          <span>Prénom : {{ match.firstNameScore }}%</span>
          <span>Nom : {{ match.lastNameScore }}%</span>
        </div>
      </div>
    }

    <!-- Options d'intervention -->
    <div class="flex flex-col gap-2">
      <span class="text-xs font-semibold uppercase tracking-wider text-neutral-11">
        Choisissez l'action à appliquer
      </span>

      <!-- Option 1 : Valider avec infos de la pièce d'identité -->
      @if (hasStripeIdentity()) {
        <button
          type="button"
          class="w-full text-left flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors"
          [class.border-jade-8]="selectedDecision() === 'VALIDATE_WITH_ID'"
          [class.bg-jade-2]="selectedDecision() === 'VALIDATE_WITH_ID'"
          [class.border-neutral-5]="selectedDecision() !== 'VALIDATE_WITH_ID'"
          [class.hover:border-neutral-7]="selectedDecision() !== 'VALIDATE_WITH_ID'"
          (click)="selectDecision('VALIDATE_WITH_ID')"
        >
          <div
            class="size-4.5 rounded-full border flex items-center justify-center shrink-0 mt-0.5"
            [class.border-jade-9]="selectedDecision() === 'VALIDATE_WITH_ID'"
            [class.bg-jade-9]="selectedDecision() === 'VALIDATE_WITH_ID'"
            [class.border-neutral-7]="selectedDecision() !== 'VALIDATE_WITH_ID'"
          >
            @if (selectedDecision() === 'VALIDATE_WITH_ID') {
              <div class="size-1.5 rounded-full bg-white"></div>
            }
          </div>
          <div class="flex flex-col min-w-0">
            <span class="font-medium text-sm text-neutral-12">
              Valider avec les informations de la pièce d'identité
            </span>
            <span class="text-xs text-neutral-10 mt-0.5">
              Écrase le prénom et nom du profil avec ceux du document officiel Stripe et valide l'identité.
            </span>
          </div>
        </button>
      }

      <!-- Option 2 : Valider profil actuel -->
      <button
        type="button"
        class="w-full text-left flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors"
        [class.border-jade-8]="selectedDecision() === 'VALIDATE_PROFILE'"
        [class.bg-jade-2]="selectedDecision() === 'VALIDATE_PROFILE'"
        [class.border-neutral-5]="selectedDecision() !== 'VALIDATE_PROFILE'"
        [class.hover:border-neutral-7]="selectedDecision() !== 'VALIDATE_PROFILE'"
        (click)="selectDecision('VALIDATE_PROFILE')"
      >
        <div
          class="size-4.5 rounded-full border flex items-center justify-center shrink-0 mt-0.5"
          [class.border-jade-9]="selectedDecision() === 'VALIDATE_PROFILE'"
          [class.bg-jade-9]="selectedDecision() === 'VALIDATE_PROFILE'"
          [class.border-neutral-7]="selectedDecision() !== 'VALIDATE_PROFILE'"
        >
          @if (selectedDecision() === 'VALIDATE_PROFILE') {
            <div class="size-1.5 rounded-full bg-white"></div>
          }
        </div>
        <div class="flex flex-col min-w-0">
          <span class="font-medium text-sm text-neutral-12">
            Valider en conservant les informations du profil
          </span>
          <span class="text-xs text-neutral-10 mt-0.5">
            Conserve le prénom et nom actuels sans modification et passe le statut à vérifié.
          </span>
        </div>
      </button>

      <!-- Option 3 : Refuser -->
      <button
        type="button"
        class="w-full text-left flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors"
        [class.border-ruby-8]="selectedDecision() === 'REJECT'"
        [class.bg-ruby-2]="selectedDecision() === 'REJECT'"
        [class.border-neutral-5]="selectedDecision() !== 'REJECT'"
        [class.hover:border-neutral-7]="selectedDecision() !== 'REJECT'"
        (click)="selectDecision('REJECT')"
      >
        <div
          class="size-4.5 rounded-full border flex items-center justify-center shrink-0 mt-0.5"
          [class.border-ruby-9]="selectedDecision() === 'REJECT'"
          [class.bg-ruby-9]="selectedDecision() === 'REJECT'"
          [class.border-neutral-7]="selectedDecision() !== 'REJECT'"
        >
          @if (selectedDecision() === 'REJECT') {
            <div class="size-1.5 rounded-full bg-white"></div>
          }
        </div>
        <div class="flex flex-col min-w-0">
          <span class="font-medium text-sm text-neutral-12"> Refuser la vérification d'identité </span>
          <span class="text-xs text-neutral-10 mt-0.5">
            Refuse la vérification. Le statut repasse à non vérifié.
          </span>
        </div>
      </button>
    </div>

    <!-- Aperçu dynamique du rendu final -->
    @let preview = previewData();
    <div class="flex flex-col gap-2.5 p-3.5 rounded-xl border border-neutral-6 bg-neutral-1 shadow-xs">
      <div class="flex items-center justify-between gap-2 border-b border-neutral-4 pb-2">
        <span class="text-xs font-bold uppercase tracking-wider text-neutral-10 flex items-center gap-1.5">
          <mat-icon class="text-primary-9 text-base! w-4! h-4!">visibility</mat-icon>
          Aperçu du rendu final après validation
        </span>
        <div
          class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-semibold border"
          [class]="preview.statusClass"
        >
          <div class="size-1.5 rounded-full" [class]="preview.statusDotClass"></div>
          {{ preview.statusLabel }}
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3 text-xs">
        <div class="flex flex-col">
          <span class="text-neutral-10">Prénom final :</span>
          <span class="font-semibold text-neutral-12 text-sm">
            {{ preview.firstName ? (preview.firstName | titlecase) : '—' }}
          </span>
        </div>
        <div class="flex flex-col">
          <span class="text-neutral-10">Nom final :</span>
          <span class="font-semibold text-neutral-12 text-sm">
            {{ preview.lastName ? (preview.lastName | titlecase) : '—' }}
          </span>
        </div>
      </div>

      <div class="text-[11px] text-neutral-11 bg-neutral-3/70 p-2 rounded-lg border border-neutral-4">
        {{ preview.impactNote }}
      </div>
    </div>
  </div>

  <div dialog-actions class="flex items-center gap-2 justify-end">
    <button
      mat-flat-button
      [class]="previewData().isDestructive ? 'primary destructive' : 'primary'"
      (click)="onConfirm()"
    >
      <mat-icon class="mr-1 text-sm! w-4! h-4!">
        {{ previewData().isDestructive ? 'close' : 'check' }}
      </mat-icon>
      Confirmer la décision
    </button>
  </div>
</aura-dialog-layout>
